import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Calendar as CalendarIcon,
  Image as ImageIcon,
  MoreHorizontal,
  Rocket,
  Send,
  Hash,
  Sparkles,
  LifeBuoy,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/Surface";
import { PlatformChipList, isMultiPlatform } from "@/components/PlatformBadges";
import usePublishEligibility from "@/lib/usePublishEligibility";
import useBillingSubscription from "@/lib/useBillingSubscription";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import { toFriendlyPublishError } from "@/lib/friendlyPublishError";

function formatScheduled(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

export default function PostCard({ post, onOpen, onAction, layout = "grid", isPublishing = false }) {
  const isPending = post.status === "pending_approval";
  const isScheduled = post.status === "scheduled";
  const isPaused = post.status === "paused";
  const isPublished = post.status === "published";
  const isFailed = post.status === "failed";
  const isPartial = post.status === "partial_published";
  const isDraft = post.status === "draft";
  const stamp = post.published_at || post.scheduled_at;
  const [reportOpen, setReportOpen] = useState(false);

  // Extract per-platform failed results (both hard failures and partial
  // publishes surface here). ``publish_results`` is a
  // ``{platform: {ok, status, publish_provider, error_message, ...}}`` map.
  // For plain single-platform 'failed' posts (no publish_results) we fall
  // back to a synthetic entry so the same rich alert renders. The RAW
  // ``error_message`` is preserved unchanged for Delivery Results.
  const failedResults = React.useMemo(() => {
    const out = [];
    const pr = post.publish_results || {};
    for (const [platform, r] of Object.entries(pr)) {
      if (r && r.ok === false) {
        out.push({ platform, error: r.error_message || post.error_message || "" });
      }
    }
    if (out.length === 0 && isFailed) {
      const single = post.platform || (post.target_platforms && post.target_platforms[0]) || "Platform";
      out.push({ platform: single, error: post.error_message || "" });
    }
    return out;
  }, [post.publish_results, post.error_message, post.platform, post.target_platforms, isFailed]);
  const hasFailure = failedResults.length > 0 && (isFailed || isPartial);

  // Publish-eligibility — only ask when there's a Publish/Retry button to gate.
  const needsEligibility = !isPublished && (isFailed || isPartial || isScheduled);
  const { eligibility } = usePublishEligibility(post.platform, { enabled: needsEligibility });
  // Multi-platform quota pre-check. For a parent targeting N platforms,
  // publishing consumes N units; if the user's remaining monthly quota
  // is smaller, block the button and explain. Reuses existing subscription
  // payload — never mutates counters. Backend still enforces via 402.
  const { sub: __postCardSub } = useBillingSubscription();
  const __targets = Array.isArray(post.target_platforms) && post.target_platforms.length > 0
    ? post.target_platforms
    : [post.platform];
  const __unitsNeeded = Math.max(1, __targets.length);
  const __monthlyRemaining = __postCardSub?.monthly_remaining;
  const multiPlatformQuotaBlocked =
    needsEligibility &&
    __unitsNeeded > 1 &&
    __monthlyRemaining != null &&
    __unitsNeeded > __monthlyRemaining;
  const multiPlatformQuotaMessage = multiPlatformQuotaBlocked
    ? `Publishing to ${__targets.join(" + ")} would use ${__unitsNeeded} posts but you only have ${__monthlyRemaining} remaining this month. Remove one platform or upgrade to continue.`
    : null;
  const canPublishNow = !needsEligibility || (!!eligibility?.can_publish && !multiPlatformQuotaBlocked);
  const blockedTitle = multiPlatformQuotaMessage
    || (!canPublishNow && eligibility?.message ? eligibility.message : undefined);
  // Show a "Retry with fewer platforms" recovery affordance on failed
  // multi-platform quota posts. Opens the existing editor so the user
  // can unselect a platform themselves — we never auto-remove one.
  const showRetryWithFewer =
    isFailed
    && post.blocked_by === "monthly_limit"
    && Array.isArray(post.target_platforms)
    && post.target_platforms.length > 1;

  // While a publish is in-flight (parent tracks this via `isPublishing`),
  // every Publish/Retry/"Fix & retry" button is disabled and shows a
  // "Publishing…" label with a spinner. This is defense-in-depth against
  // rapid double-clicks (the backend also holds a per-post lock).
  const publishDisabled = !canPublishNow || isPublishing;
  const publishTitle = isPublishing
    ? "This post is already publishing. Please wait for it to finish."
    : blockedTitle;

  if (layout === "list") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -1 }}
        className="group flex items-center gap-3 rounded-[14px] glass glass-hover p-3 cursor-pointer"
        onClick={() => onOpen?.(post)}
        data-testid="post-card-list"
      >
        {post.image_url ? (
          <img src={post.image_url} alt="" className="h-14 w-14 rounded-md object-cover border border-[color:var(--border-subtle)] shrink-0" data-testid="post-card-image" />
        ) : (
          <div className="h-14 w-14 rounded-md bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)] shrink-0">
            <ImageIcon className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge
            status={post.status}
            publishProvider={post.publish_provider}
            isSimulated={!!post.is_simulated_publish}
          />
            {isMultiPlatform(post) ? (
              <PlatformChipList post={post} data-testid="post-card-platforms-list" />
            ) : (
              <span className="text-[10px] text-[color:var(--text-3)]">{post.platform}</span>
            )}
            {post.source === "autopilot" ? (
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--brand-secondary)] inline-flex items-center gap-1"><Rocket className="h-3 w-3" /> auto</span>
            ) : null}
            {post.publish_provider === "demo" ? (
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--accent-warm)]" data-testid="post-card-demo-badge-list">demo</span>
            ) : null}
          </div>
          <div className="text-sm text-[color:var(--text-1)] line-clamp-1 mt-1">{post.caption}</div>
          {stamp ? (
            <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 inline-flex items-center gap-1">
              <CalendarIcon className="h-3 w-3" /> {formatScheduled(stamp)}
            </div>
          ) : null}
        </div>
        <PostCardMenu post={post} onAction={onAction} onOpen={onOpen} />
      </motion.div>
    );
  }

  // Default: grid
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      className="group rounded-[16px] glass glass-hover overflow-hidden cursor-pointer flex flex-col"
      onClick={() => onOpen?.(post)}
      data-testid="post-card"
    >
      <div className="relative">
        {post.image_url ? (
          <img src={post.image_url} alt="" className="w-full aspect-square object-cover" data-testid="post-card-image" />
        ) : (
          <div className="w-full aspect-square bg-gradient-to-br from-[color:var(--surface-3)] to-[color:var(--surface-2)] flex items-center justify-center text-[color:var(--text-3)]">
            <ImageIcon className="h-7 w-7" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <StatusBadge
            status={post.status}
            publishProvider={post.publish_provider}
            isSimulated={!!post.is_simulated_publish}
          />
        </div>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <PostCardMenu post={post} onAction={onAction} onOpen={onOpen} />
        </div>
        {post.source === "autopilot" ? (
          <div className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wider text-[color:var(--bg-0)] bg-[color:var(--brand-secondary)] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-semibold">
            <Rocket className="h-3 w-3" /> Autopilot
          </div>
        ) : null}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="text-sm text-[color:var(--text-1)] line-clamp-3 whitespace-pre-line">{post.caption}</div>
        {post.hashtags?.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {post.hashtags.slice(0, 5).map((h, i) => (
              <span key={i} className="text-[10px] rounded-full border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] px-1.5 py-0.5">{h}</span>
            ))}
            {post.hashtags.length > 5 ? (
              <span className="text-[10px] text-[color:var(--text-3)] px-1">+{post.hashtags.length - 5}</span>
            ) : null}
          </div>
        ) : null}
        <div className="mt-auto pt-3 flex items-center justify-between text-[11px] text-[color:var(--text-3)]">
          <span className="inline-flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />
            {stamp ? formatScheduled(stamp) : isDraft ? "Draft" : "—"}
          </span>
          {isMultiPlatform(post) ? (
            <PlatformChipList post={post} data-testid="post-card-platforms-grid" />
          ) : (
            <span>{post.platform}</span>
          )}
        </div>
        {/* Failure surface (truthful publish state). Shown for both
             'failed' and 'partial_published' posts. Each failed platform
             gets its own row with a friendly explanation + a "Fix & retry"
             action. Raw Meta errors are ALWAYS preserved in Delivery
             Results (inside the post editor) for troubleshooting. */}
        {hasFailure ? (
          <div
            className="mt-2 rounded-md border border-[color:rgba(255,79,82,0.35)] bg-[color:rgba(255,79,82,0.06)] px-2 py-2 text-[11px] text-[color:var(--danger)] leading-snug space-y-1.5"
            data-testid="post-card-error"
          >
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {isPartial
                ? "Partially published — some platforms failed"
                : failedResults.length === 1
                ? `${failedResults[0].platform} failed to publish`
                : "Publish failed on multiple platforms"}
            </div>
            {failedResults.map((r, idx) => {
              const friendly = toFriendlyPublishError(r.platform, r.error);
              return (
                <div
                  key={`${r.platform}-${idx}`}
                  className="rounded-sm bg-[color:rgba(255,79,82,0.08)] px-2 py-1.5"
                  data-testid={`post-card-error-${r.platform.toLowerCase()}`}
                >
                  <div className="font-semibold text-[color:var(--text-1)]">
                    {r.platform} failed to publish
                  </div>
                  <div className="mt-0.5 text-[color:var(--text-2)]">
                    {friendly.explanation}
                  </div>
                </div>
              );
            })}
            <div className="flex items-center gap-2 pt-0.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen?.(post);
                }}
                className="inline-flex items-center gap-1 rounded border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)]"
                data-testid="post-card-view-error"
              >
                View error
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!publishDisabled) onAction?.("publish", post);
                }}
                disabled={publishDisabled}
                title={publishTitle}
                className="inline-flex items-center gap-1 rounded bg-[color:var(--brand-primary)] px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-[color:var(--bg-0)] hover:bg-[color:var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="post-card-fix-retry"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Fix & retry
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReportOpen(true);
                }}
                className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-2)] hover:text-[color:var(--text-1)] underline-offset-2 hover:underline"
                data-testid="post-card-report-issue"
              >
                <LifeBuoy className="h-3 w-3" /> Report
              </button>
            </div>
          </div>
        ) : null}
        {/* Live external link when we have a confirmed external_post_url */}
        {isPublished && post.external_post_url ? (
          <a
            href={post.external_post_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mt-2 text-[11px] text-[color:var(--accent-warm)] hover:underline inline-flex items-center gap-1"
            data-testid="post-card-external-link"
          >
            View on {post.publish_provider === "x" ? "X" : "platform"} ↗
          </a>
        ) : null}
        {/* Quick action bar */}
        <div className="mt-3 flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {isPending ? (
            <>
              <Button size="sm" className="flex-1 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)]" onClick={() => onAction?.("approve", post)} data-testid="post-card-approve">Approve</Button>
              <Button size="sm" variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-2)]" onClick={() => onAction?.("reject", post)} data-testid="post-card-reject">Reject</Button>
            </>
          ) : isPaused ? (
            <Button size="sm" variant="outline" className="flex-1 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => onAction?.("resume", post)} data-testid="post-card-resume">Resume</Button>
          ) : isFailed || isPartial ? (
            <>
              <Button
                size="sm"
                className="flex-1 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { if (!publishDisabled) onAction?.("publish", post); }}
                disabled={publishDisabled}
                title={publishTitle}
                data-testid="post-card-retry"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1" />Retry
                  </>
                )}
              </Button>
              <Button size="sm" variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => onOpen?.(post)} data-testid="post-card-edit-failed">Edit</Button>
              {showRetryWithFewer ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[color:rgba(255,138,61,0.35)] bg-[color:var(--surface-2)] text-[color:var(--accent-warm)]"
                  onClick={() => onOpen?.(post)}
                  data-testid="post-card-retry-fewer-platforms"
                  title="Open the editor to unselect a platform and try again."
                >
                  Retry with fewer platforms
                </Button>
              ) : null}
            </>
          ) : isScheduled ? (
            <>
              <Button size="sm" variant="outline" className="flex-1 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => onAction?.("pause", post)} data-testid="post-card-pause">Pause</Button>
              <Button
                size="sm"
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => { if (!publishDisabled) onAction?.("publish", post); }}
                disabled={publishDisabled}
                title={publishTitle}
                data-testid="post-card-publish"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1" />Now
                  </>
                )}
              </Button>
            </>
          ) : isPublished ? (
            <Button size="sm" variant="outline" className="flex-1 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => onAction?.("duplicate", post)} data-testid="post-card-duplicate">Duplicate</Button>
          ) : (
            <Button size="sm" variant="outline" className="flex-1 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" onClick={() => onOpen?.(post)} data-testid="post-card-edit">Edit</Button>
          )}
        </div>
      </div>
      <ReportProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        context={{
          category: "publishing",
          subject: `Publish failed on ${post.platform || "platform"}: ${post.post_id || ""}`.slice(0, 200),
          message:
            (post.error_message ? `Error: ${post.error_message}\n\n` : "") +
            `Post ID: ${post.post_id || "—"}\nPlatform: ${post.platform || "—"}\n\nWhat were you trying to do?`,
          platform: post.platform,
          relatedPostId: post.post_id,
          relatedPublishId: post.external_post_id,
        }}
      />
    </motion.div>
  );
}

function PostCardMenu({ post, onAction, onOpen }) {
  const isPending = post.status === "pending_approval";
  const isScheduled = post.status === "scheduled";
  const isPaused = post.status === "paused";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 bg-[color:var(--bg-1)]/70 backdrop-blur text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)]" data-testid="post-card-menu">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[color:var(--surface-1)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" align="end">
        <DropdownMenuItem onClick={() => onOpen?.(post)} data-testid="menu-edit">Open editor</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction?.("regenerate-caption", post)} data-testid="menu-regen-caption" title="Uses 1 of your remaining AI generations.">
          <Sparkles className="h-3.5 w-3.5 mr-2" /> Regenerate caption
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction?.("regenerate-image", post)} data-testid="menu-regen-image" title="Uses 1 of your remaining AI generations.">
          <ImageIcon className="h-3.5 w-3.5 mr-2" /> Regenerate image
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction?.("regenerate", post)} data-testid="menu-regen-all" title="Uses 1 AI credit for the caption and 1 for the image.">
          <Hash className="h-3.5 w-3.5 mr-2" /> Regenerate everything
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[color:var(--border-subtle)]" />
        {isPending ? (
          <>
            <DropdownMenuItem onClick={() => onAction?.("approve", post)} data-testid="menu-approve">Approve</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction?.("reject", post)} data-testid="menu-reject">Reject</DropdownMenuItem>
          </>
        ) : null}
        {isScheduled ? <DropdownMenuItem onClick={() => onAction?.("pause", post)} data-testid="menu-pause">Pause</DropdownMenuItem> : null}
        {isPaused ? <DropdownMenuItem onClick={() => onAction?.("resume", post)} data-testid="menu-resume">Resume</DropdownMenuItem> : null}
        <DropdownMenuItem onClick={() => onAction?.("duplicate", post)} data-testid="menu-duplicate">Duplicate</DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[color:var(--border-subtle)]" />
        <DropdownMenuItem onClick={() => onAction?.("delete", post)} className="text-[color:var(--danger)] focus:text-[color:var(--danger)]" data-testid="menu-delete">Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
